// =============================================================================
// Main App Component
// =============================================================================

import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { useTokenRefresh } from './hooks/useTokenRefresh';

// Layout
import { AppLayout } from './components/layout/AppLayout';

// Auth Pages
import { LoginPage } from './pages/auth/LoginPage';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage';
import { ActivateAccountPage } from './pages/auth/ActivateAccountPage';

// Investor Pages
import {
  DashboardPage,
  InvestmentsPage,
  InvestmentDetailPage,
  DocumentsPage,
  DistributionsPage,
  ProfilePage,
  AnnouncementsPage,
  MessagesPage,
} from './pages/investor';

// Admin Pages
import {
  AdminDashboardPage,
  InvestorsPage as AdminInvestorsPage,
  InvestorImportPage as AdminInvestorImportPage,
  InvestorDetailPage as AdminInvestorDetailPage,
  DealsPage as AdminDealsPage,
  DealDetailPage as AdminDealDetailPage,
  DocumentsPage as AdminDocumentsPage,
  DocumentBulkUploadPage as AdminDocumentBulkUploadPage,
  DistributionsPage as AdminDistributionsPage,
  AnnouncementsPage as AdminAnnouncementsPage,
  MessagesPage as AdminMessagesPage,
  TempAccessPage as AdminTempAccessPage,
  AuditLogPage,
  SettingsPage as AdminSettingsPage,
} from './pages/admin';

// Accountant Pages
import {
  AccountantDashboardPage,
  AccountantDealPage,
  AccountantInvestorsPage,
  AccountantInvestorDetailPage,
} from './pages/accountant';

// Fundraising Pages (Phase II)
import {
  ContactsPage as FundraisingContactsPage,
  ContactDetailPage as FundraisingContactDetailPage,
  PipelinePage as FundraisingPipelinePage,
  CampaignsPage as FundraisingCampaignsPage,
  CampaignDetailPage as FundraisingCampaignDetailPage,
  ConversionPage as FundraisingConversionPage,
} from './pages/admin/fundraising';

// Prospect Pages (Phase II)
import {
  DataRoomListPage as ProspectDataRoomListPage,
  DataRoomDetailPage as ProspectDataRoomDetailPage,
  NdaSignPage as ProspectNdaSignPage,
} from './pages/prospect';

// E-Signature Pages
import SigningCeremonyPage from './pages/signing/SigningCeremonyPage';
import TemplatesPage from './pages/admin/esign/TemplatesPage';
import TemplateBuilderPage from './pages/admin/esign/TemplateBuilderPage';
import EnvelopesPage from './pages/admin/esign/EnvelopesPage';
import CreateEnvelopePage from './pages/admin/esign/CreateEnvelopePage';
import EnvelopeDetailPage from './pages/admin/esign/EnvelopeDetailPage';

// =============================================================================
// Protected Route Component
// =============================================================================

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'ADMIN' | 'TEAM' | 'INVESTOR' | 'TEMP_ACCESS' | 'PROSPECT';
}

function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const { isAuthenticated, isInitialized, user } = useAuth();
  const location = useLocation();

  // Wait for auth to be initialized before deciding
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-900" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requiredRole && user?.role !== requiredRole) {
    // TEAM users can access ADMIN routes (permission-gated on backend)
    if (requiredRole === 'ADMIN' && user?.role === 'TEAM') {
      return <>{children}</>;
    }
    // Redirect to appropriate dashboard based on role
    if (user?.role === 'ADMIN') {
      return <Navigate to="/admin" replace />;
    }
    if (user?.role === 'TEAM') {
      return <Navigate to="/admin" replace />;
    }
    if (user?.role === 'TEMP_ACCESS') {
      return <Navigate to="/accountant" replace />;
    }
    if (user?.role === 'PROSPECT') {
      return <Navigate to="/prospect/datarooms" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

// =============================================================================
// Public Route (redirects if authenticated)
// =============================================================================

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isInitialized, user } = useAuth();

  // Wait for auth to be initialized before deciding
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-900" />
      </div>
    );
  }

  if (isAuthenticated) {
    // Redirect to appropriate dashboard
    if (user?.role === 'ADMIN' || user?.role === 'TEAM') {
      return <Navigate to="/admin" replace />;
    }
    if (user?.role === 'TEMP_ACCESS') {
      return <Navigate to="/accountant" replace />;
    }
    if (user?.role === 'PROSPECT') {
      return <Navigate to="/prospect/datarooms" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

// =============================================================================
// App Component
// =============================================================================

export default function App() {
  const { checkAuth, isInitialized } = useAuth();

  // Start proactive token refresh timer
  useTokenRefresh();

  useEffect(() => {
    // Only check auth once on mount
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Show loading only until auth is initialized (one-time check)
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-900 mx-auto" />
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        }
      />
      <Route
        path="/activate/:token"
        element={
          <PublicRoute>
            <ActivateAccountPage />
          </PublicRoute>
        }
      />

      {/* Public Signing Route (token-based, no auth) */}
      <Route path="/sign/:token" element={<SigningCeremonyPage />} />

      {/* Investor Routes */}
      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/investments" element={<InvestmentsPage />} />
        <Route path="/investments/:id" element={<InvestmentDetailPage />} />
        <Route path="/documents" element={<DocumentsPage />} />
        <Route path="/distributions" element={<DistributionsPage />} />
        <Route path="/announcements" element={<AnnouncementsPage />} />
        <Route path="/messages" element={<MessagesPage />} />
        <Route path="/profile" element={<ProfilePage />} />
      </Route>

      {/* Admin Routes */}
      <Route
        element={
          <ProtectedRoute requiredRole="ADMIN">
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/admin" element={<AdminDashboardPage />} />
        <Route path="/admin/investors" element={<AdminInvestorsPage />} />
        <Route path="/admin/investors/import" element={<AdminInvestorImportPage />} />
        <Route path="/admin/investors/:investorId" element={<AdminInvestorDetailPage />} />
        <Route path="/admin/deals" element={<AdminDealsPage />} />
        <Route path="/admin/deals/:id" element={<AdminDealDetailPage />} />
        <Route path="/admin/documents" element={<AdminDocumentsPage />} />
        <Route path="/admin/documents/bulk-upload" element={<AdminDocumentBulkUploadPage />} />
        <Route path="/admin/distributions" element={<AdminDistributionsPage />} />
        <Route path="/admin/announcements" element={<AdminAnnouncementsPage />} />
        <Route path="/admin/messages" element={<AdminMessagesPage />} />
        <Route path="/admin/settings" element={<AdminSettingsPage />} />
        <Route path="/admin/temp-access" element={<AdminTempAccessPage />} />
        <Route path="/admin/audit-log" element={<AuditLogPage />} />
        {/* Fundraising (Phase II) */}
        <Route path="/admin/fundraising/contacts" element={<FundraisingContactsPage />} />
        <Route path="/admin/fundraising/contacts/:contactId" element={<FundraisingContactDetailPage />} />
        <Route path="/admin/fundraising/pipeline" element={<FundraisingPipelinePage />} />
        <Route path="/admin/fundraising/campaigns" element={<FundraisingCampaignsPage />} />
        <Route path="/admin/fundraising/campaigns/:campaignId" element={<FundraisingCampaignDetailPage />} />
        <Route path="/admin/fundraising/conversion" element={<FundraisingConversionPage />} />
        {/* E-Signatures */}
        <Route path="/admin/esign/templates" element={<TemplatesPage />} />
        <Route path="/admin/esign/templates/new" element={<TemplateBuilderPage />} />
        <Route path="/admin/esign/templates/:templateId" element={<TemplateBuilderPage />} />
        <Route path="/admin/esign/envelopes" element={<EnvelopesPage />} />
        <Route path="/admin/esign/envelopes/new" element={<CreateEnvelopePage />} />
        <Route path="/admin/esign/envelopes/:envelopeId" element={<EnvelopeDetailPage />} />
      </Route>

      {/* Accountant Routes (Temporary Access) */}
      <Route
        element={
          <ProtectedRoute requiredRole="TEMP_ACCESS">
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/accountant" element={<AccountantDashboardPage />} />
        <Route path="/accountant/deals/:dealId" element={<AccountantDealPage />} />
        <Route path="/accountant/deals/:dealId/investors/:profileId" element={<AccountantInvestorDetailPage />} />
        <Route path="/accountant/investors" element={<AccountantInvestorsPage />} />
        <Route path="/accountant/investors/:profileId" element={<AccountantInvestorDetailPage />} />
      </Route>

      {/* Prospect Routes (Phase II) */}
      <Route
        element={
          <ProtectedRoute requiredRole="PROSPECT">
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/prospect/datarooms" element={<ProspectDataRoomListPage />} />
        <Route path="/prospect/datarooms/:dealId" element={<ProspectDataRoomDetailPage />} />
        <Route path="/prospect/datarooms/:dealId/nda" element={<ProspectNdaSignPage />} />
      </Route>

      {/* Default Redirects */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
    </>
  );
}
